// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/oriora_flashcards/generate-cards
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Decks, cards, and the whole review history stay
// local; only the NOTES TEXT the user pasted (or the on-device OCR of a photographed
// notes page — the photo itself NEVER leaves the device) is sent for THIS one call.
//
// ONE billed AI call per user action. CRUD, not agentic: text in, JSON cards out.
// No agent loop, no 2nd call, no tool-use chain, no direct OpenAI/Anthropic SDK here.
//
// HARDEST guardrails:
//   • STUDY-TOOL fence: the brain makes STUDY CARDS that teach the concepts in the
//     user's own notes — it NEVER writes essays, completes assignments, or produces
//     do-the-homework answers. A hard PRE-LLM refusal (no billed call) catches the
//     obvious "write my essay / do my homework" asks — see academicRefusal() below +
//     the matching server-side prompt rule (second line of defence).
//   • The pasted notes are UNTRUSTED DATA to the model, never instructions (the
//     server wraps them in a data fence on top of the shared INJECTION_FENCE).

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";

const SERVER = "https://api.orioralabs.com";
const APP_SLUG = "oriora_flashcards";

export type BrainErrorCode =
  | "insufficient_funds"
  | "managed_ai_blocked"
  | "unauthenticated"
  | "unavailable"
  | "refused_academic"
  | "no_store"
  | "too_large";

export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Heartbeat endpoints (/generate-cards) lock the HTTP status to 200 once keepalive bytes
    // flush and encode failures IN the body via `_status` — same contract as workout_tracker
    // /draft_program. Leading whitespace IS the heartbeat: trim, parse, then map failures back
    // to the same BrainError classes the status switch above produces, so a failed call can
    // never be mistaken for "the notes produced zero cards".
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    const text = (await res.text()).trim();
    let parsed: Record<string, unknown>;
    try { parsed = JSON.parse(text) as Record<string, unknown>; } catch { throw new BrainError("unavailable", "AI is busy — please try again."); }
    const bodyStatus = typeof parsed._status === "number" ? parsed._status : undefined;
    if (bodyStatus === 402) throw new BrainError("insufficient_funds", String(parsed.message ?? "Add funds to keep using AI."));
    if (bodyStatus === 403) throw new BrainError("managed_ai_blocked", String(parsed.message ?? "Connect a vendor key in Oriora settings to use AI for this app."));
    if (bodyStatus !== undefined) throw new BrainError("unavailable", String(parsed.message ?? parsed.error ?? "AI request failed."));
    return parsed as unknown as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

// ── STUDY-TOOL fence (hard, PRE-LLM) ────────────────────────────────────────────
// Flashcards turns YOUR notes into study cards; it is NOT an essay mill or a
// do-my-homework machine. The obvious asks are refused BEFORE any billed call, so
// neither we nor the model ever produce completed assignments. (Mirrored server-side —
// the server prompt refuses the subtler cases the patterns can't catch.)
const ACADEMIC_PATTERNS: RegExp[] = [
  // Allow up to 3 filler words between the article and the noun so "write me a 500-word paper"
  // / "write me a long essay" are caught, without over-blocking "write me a card about the essay".
  /\bwrite\s+(my|me|this|the|an?)\s+(\w+\s+){0,3}(essay|paper|report|thesis|dissertation|assignment|homework|coursework)\b/i,
  /\bdo\s+my\s+(homework|assignment|essay|coursework|test|exam|quiz)\b/i,
  /\b(solve|complete|finish|answer)\s+(my|this|the)\s+(homework|assignment|worksheet|problem\s*set|exam|test|quiz|coursework|essay|paper|report)\b/i,
  /\banswer\s*key\b/i,
  /\bwrite\s+(the\s+)?answers?\s+(to|for)\b/i,
  /\b\d+\s*[- ]?word\s+essay\b/i,
];

const ACADEMIC_REFUSAL =
  "Flashcards makes study cards that teach the ideas in your notes — it doesn't write essays or do assignments.";

/** Returns the refusal message if the pasted text is plainly a do-the-work ask,
 *  else null. The caller blocks the AI call when this returns a string. */
export function academicRefusal(notes: string): string | null {
  const t = notes.trim();
  if (!t) return null;
  return ACADEMIC_PATTERNS.some((re) => re.test(t)) ? ACADEMIC_REFUSAL : null;
}

export interface GeneratedCard { front: string; back: string; tag: string }

/**
 * Generate study cards from pasted notes — the ONE AI feature.
 *
 * The notes text (typed/pasted, or produced by the ON-DEVICE OCR of a photographed
 * page — the image never leaves the device) is sent to the brain, which runs ONE AI
 * call and returns a JSON array of {front, back, tag} study cards. The user reviews
 * the preview and chooses what to save — nothing is auto-inserted.
 *
 * Endpoint: POST /api/apps/oriora_flashcards/generate-cards  (Bearer JWT, bills the wallet).
 */
export async function generateCards(notes: string): Promise<GeneratedCard[]> {
  const text = notes.trim();
  // Hard pre-LLM refusal — no billed call for do-my-homework asks.
  const refusal = academicRefusal(text);
  if (refusal) throw new BrainError("refused_academic", refusal);
  if (text.length < 30) {
    throw new BrainError("unavailable", "Paste a bit more — a few sentences of notes is enough to work with.");
  }
  const r = await brainCall<{ cards?: unknown; refusal?: unknown }>("generate-cards", { notes: text });
  if (typeof r.refusal === "string" && r.refusal.trim()) {
    throw new BrainError("refused_academic", r.refusal.trim());
  }
  const raw = Array.isArray(r.cards) ? r.cards : [];
  return raw
    .map((c) => {
      const o = (c ?? {}) as Record<string, unknown>;
      return {
        front: typeof o.front === "string" ? o.front.trim() : "",
        back: typeof o.back === "string" ? o.back.trim() : "",
        tag: typeof o.tag === "string" ? o.tag.trim().toLowerCase() : "",
      };
    })
    .filter((c) => c.front && c.back)
    .slice(0, 40);
}

// ── Cloud backup (the user owns their data) ─────────────────────────────────────
// The device builds the FAITHFUL backup locally (export_backup); these calls relay it to the
// user's OWN connected cloud store — their GitHub repo or Google Drive (connected once in the
// Oriora account/Connections hub; the server picks whichever). Oriora RETAINS nothing. Plain
// HTTP → works on iOS with no file picker. Endpoints: POST/GET /api/apps/oriora_flashcards/backup.
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }

async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/${APP_SLUG}/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}

/** Save the local backup envelope to the user's connected cloud store. Throws BrainError('no_store')
 *  when nothing is connected, so the UI can point them at the Connections hub. */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ data: envelope }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN cloud
 *  store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora retains
 *  nothing (text relay only). Throws no_store when nothing is connected. Endpoint: the shared
 *  POST /api/apps/oriora_flashcards/store route (distinct from the whole-collection `backup` route). */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + ipc.rs).
// The web callback must allowlist `oriora-flashcards` as a return target (landing RETURN_APPS,
// operator task).
export const openWebLogin = () => invoke<void>("open_login_window");
